import React from "react";
import i18n from "i18next";
import {
  isExperienceEditorActive,
  withSitecoreContext,
} from "@sitecore-jss/sitecore-jss-react";
import { Auth0Provider } from "@auth0/auth0-react";
import { connect } from "react-redux";
import { getWindow } from "ssr-window";

import { getRouteData } from "./dataApi";
import config from "./temp/config";
import Layout from "./Layout";
import auth from "./utils/auth";
import { parse } from "./utils";
import AuthHandler from "./AuthHandler";
import { modeEnum } from "@/utils/enum";
import {
  updateMode as updateModeDispatch,
  updateAppState as updateAppStateDispatch,
} from "@/models/config";
import url from "@/utils/url";

/* eslint-disable no-console */

// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static React routing isn't enough -
// we need to be able to load dynamic route data from Sitecore after the client side route changes.
// So react-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.

class RouteHandler extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      notFound: true,
      defaultLanguage: config.defaultLanguage,
    };

    const routeData = this.extractRouteData();

    // route data from react-router - if route was resolved, it's not a 404
    if (props.route !== null) {
      this.state.notFound = false;
    }

    // if we have an initial SSR state, and that state doesn't have a valid route data,
    // then this is a 404 route.
    if (routeData && (!routeData.sitecore || !routeData.sitecore.route)) {
      this.state.notFound = true;
    }

    // if we have an SSR state, and that state has language data, set the current language
    // (this makes the language of content follow the Sitecore context language cookie)
    // note that a route-based language (i.e. /de-DE) will override this default; this is for home.
    if (routeData?.sitecore?.context?.language) {
      this.state.defaultLanguage = routeData.sitecore.context.language;
    }

    // tell i18next to sync its current language with the route language
    this.updateLanguage();

    this.updateProviderConfig();

    this.updateScMode();
  }

  componentDidMount() {
    const routeData = this.extractRouteData();

    // if no existing routeData is present (from SSR), get Layout Service fetching the route data or ssr render complete
    if (!routeData || this.props.ssrRenderComplete) {
      this.updateRouteData();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const url = this.props?.route?.match?.params.sitecoreRoute;
    const nextUrl = nextProps?.route?.match?.params.sitecoreRoute;
    const itemId = this.props?.sitecoreContext?.itemId;
    const nextItemId = nextProps?.sitecoreContext?.itemId;
    const state = this.props?.route?.location?.state;
    const nState = nextProps?.route?.location?.state;
    if (
      url &&
      nextUrl &&
      url !== nextUrl &&
      itemId &&
      nextItemId &&
      itemId === nextItemId
    ) {
      // itemIDのみが不一致のばあい
      if (isExperienceEditorActive()) {
        window.location.assign(nextProps.route.match.url);
        return;
      }

      this.updateLanguage(nextProps.route.match.params.lang);
      this.updateRouteData(nextUrl);
      return false;
    } else if (
      url &&
      nextUrl &&
      url === nextUrl &&
      itemId &&
      nextItemId &&
      itemId === nextItemId &&
      state === nState
    ) {
      return true;
    } else {
      return true;
    }
  }

  componentDidUpdate(previousProps) {
    const existingRoute = previousProps.route.match.url;
    const newRoute = this.props.route.match.url;
    const existingState = previousProps.route.location.state;
    const newState = this.props.route.location.state;

    // don't change state (refetch route data) if the route has not changed
    if (existingRoute === newRoute && existingState === newState) {
      return;
    }

    // if in experience editor - force reload instead of route data update
    // avoids confusing Sitecore's editing JS
    if (isExperienceEditorActive()) {
      window.location.assign(newRoute);
      return;
    }

    this.updateLanguage();
    this.updateRouteData();
  }

  /**
   * Updates the current app language to match the route data.
   */
  updateLanguage(lang) {
    const newLanguage =
      lang || this.props.route.match.params.lang || this.state.defaultLanguage;

    if (
      i18n.language !== newLanguage &&
      ["ja-JP", "en"].indexOf(newLanguage) !== -1
    ) {
      i18n.changeLanguage(newLanguage);
    }
  }

  updateProviderConfig() {
    this.providerConfig = {
      domain: auth.domain,
      clientId: auth.clientId,
      scope: auth.scope,
      useRefreshTokens: true,
      redirectUri: `${getWindow()?.location?.origin}${url.home}`,
      advancedOptions: { defaultScope: auth.scope },
      ...(auth.audience ? { audience: auth.audience } : null),
      onRedirectCallback: (appState) => {
        const { updateAppState } = this.props;
        appState && updateAppState({ appState });
      },
    };
  }

  updateScMode() {
    let mode =
      parse("sc_mode", this.props?.route?.location?.search) ||
      process.env.REACT_APP_DEV ||
      modeEnum.normal;
    if (isExperienceEditorActive()) {
      mode = modeEnum.pageEditing;
    }
    // アクセスモードの更新
    const { updateMode } = this.props;
    updateMode({
      mode,
    });
    this.scMode = mode;
  }

  /**
   * Loads route data from Sitecore Layout Service into state.routeData
   */
  updateRouteData(url) {
    let sitecoreRoutePath =
      url || this.props.route.match.params.sitecoreRoute || "/";
    if (!sitecoreRoutePath.startsWith("/")) {
      sitecoreRoutePath = `/${sitecoreRoutePath}`;
    }

    const language =
      this.props.route.match.params.lang || this.state.defaultLanguage;

    // get the route data for the new route
    getRouteData(sitecoreRoutePath, language).then((routeData) => {
      if (routeData?.sitecore?.route) {
        // set the sitecore context data and push the new route
        this.props.updateSitecoreContext({
          route: routeData.sitecore.route,
          itemId: routeData.sitecore.route.itemId,
          ...routeData.sitecore.context,
        });
        this.setState({ notFound: false });
      } else {
        this.setState({ notFound: true }, () =>
          this.props.updateSitecoreContext(routeData?.sitecore?.context)
        );
      }
    });

    window.scrollTo(0, 0);
  }

  extractRouteData = () => {
    if (!this.props.sitecoreContext) return null;

    const { route, ...context } = this.props.sitecoreContext;

    return {
      sitecore: {
        route,
        context,
      },
    };
  };

  layout = (routeData) => {
    return (
      <Layout
        router={this.props.route}
        route={routeData.sitecore.route}
        sitecoreContext={routeData.sitecore.context}
        ssrRenderComplete={this.props.ssrRenderComplete}
      />
    );
  };

  render() {
    // no route data for the current route in Sitecore - show not found component.
    // Note: this is client-side only 404 handling. Server-side 404 handling is the responsibility
    // of the server being used (i.e. node-headless-ssr-proxy and Sitecore intergrated rendering know how to send 404 status codes).
    const { notFound } = this.state;
    if (notFound) {
      window.location.href = url.notFound;
    }

    // Don't render anything if the route data or dictionary data is not fully loaded yet.
    // This is a good place for a "Loading" component, if one is needed.
    const routeData = this.extractRouteData();
    if (!routeData) {
      return <div />;
    }

    if (this.scMode !== modeEnum.normal) {
      if (this.scMode === modeEnum.dev) {
        require("../src/mock");
      }
      return this.layout(routeData);
    }

    return (
      <Auth0Provider {...this.providerConfig}>
        <AuthHandler>{this.layout(routeData)}</AuthHandler>
      </Auth0Provider>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    updateMode: (action) => dispatch(updateModeDispatch(action)),
    updateAppState: (action) => dispatch(updateAppStateDispatch(action)),
  };
};

export default withSitecoreContext({ updatable: true })(
  connect(null, mapDispatchToProps)(RouteHandler)
);
